/** Pure helpers for dine-in table assignment and overlap checks (stores + dashboard). */
function intervalsOverlap(a, b) {
    return a.start < b.end && b.start < a.end;
}
export function dineInTableCapacity(t, fallback = 4) {
    return t.seats && t.seats > 0 ? t.seats : fallback;
}
/**
 * Bookings that occupy physical tables for overlap purposes.
 */
function bookingOccupiesTables(b) {
    if (b.status === "cancelled" || b.status === "complete")
        return false;
    const ids = b.table_ids;
    return !!(ids && ids.length);
}
function turnMs(b, defaultTurnMinutes) {
    const m = b.table_turn_minutes && b.table_turn_minutes > 0 ? b.table_turn_minutes : defaultTurnMinutes;
    return m * 60_000;
}
export function buildOccupiedByTableFromBookings(bookings, defaultTurnMinutes, excludeBookingId) {
    const map = new Map();
    for (const b of bookings) {
        if (excludeBookingId && b._id === excludeBookingId)
            continue;
        if (!bookingOccupiesTables(b))
            continue;
        const iv = {
            start: b.config.timestamp,
            end: b.config.timestamp + turnMs(b, defaultTurnMinutes),
        };
        for (const tid of b.table_ids) {
            if (!map.has(tid))
                map.set(tid, []);
            map.get(tid).push(iv);
        }
    }
    return map;
}
function tableFreeForWindow(tableId, window, occupied) {
    const list = occupied.get(tableId);
    if (!list || !list.length)
        return true;
    return !list.some((iv) => intervalsOverlap(iv, window));
}
export function tryAssignTablesForParty(tables, party, window, occupied, defaultSeatFallback = 4) {
    const singles = tables
        .map((t) => ({ t, c: dineInTableCapacity(t, defaultSeatFallback) }))
        .filter(({ c }) => c >= party)
        .sort((a, b) => a.c - b.c);
    for (const { t } of singles) {
        if (tableFreeForWindow(t._id, window, occupied))
            return [t._id];
    }
    const ranked = [...tables].sort((a, b) => dineInTableCapacity(b, defaultSeatFallback) - dineInTableCapacity(a, defaultSeatFallback));
    let sum = 0;
    const picked = [];
    for (const t of ranked) {
        if (sum >= party)
            break;
        if (!tableFreeForWindow(t._id, window, occupied))
            continue;
        picked.push(t._id);
        sum += dineInTableCapacity(t, defaultSeatFallback);
    }
    if (sum < party)
        return null;
    while (picked.length > 1) {
        const last = picked[picked.length - 1];
        const lastT = tables.find((x) => x._id === last);
        const capWithout = sum - dineInTableCapacity(lastT, defaultSeatFallback);
        if (capWithout >= party) {
            sum = capWithout;
            picked.pop();
        }
        else
            break;
    }
    return picked;
}
export function validateExplicitTableAssignment(table_ids, tables, party, window, occupied, defaultSeatFallback = 4) {
    if (!table_ids.length)
        return false;
    const idSet = new Set(tables.map((t) => t._id));
    for (const id of table_ids) {
        if (!idSet.has(id))
            return false;
    }
    let cap = 0;
    for (const id of table_ids) {
        const t = tables.find((x) => x._id === id);
        if (!t)
            return false;
        if (!tableFreeForWindow(id, window, occupied))
            return false;
        cap += dineInTableCapacity(t, defaultSeatFallback);
    }
    return cap >= party;
}
export function listMoveTableOptions(tables, party, window, occupied, defaultSeatFallback = 4) {
    const out = [];
    const seen = new Set();
    const push = (ids) => {
        const key = [...ids].sort().join(",");
        if (seen.has(key))
            return;
        seen.add(key);
        const labels = ids.map((id) => {
            const t = tables.find((x) => x._id === id);
            return (t?.name && t.name.trim()) || id;
        });
        out.push({ table_ids: ids, label: labels.join(" + ") });
    };
    for (const t of tables) {
        const c = dineInTableCapacity(t, defaultSeatFallback);
        if (c >= party && tableFreeForWindow(t._id, window, occupied))
            push([t._id]);
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            const a = tables[i];
            const b = tables[j];
            const ca = dineInTableCapacity(a, defaultSeatFallback);
            const cb = dineInTableCapacity(b, defaultSeatFallback);
            if (ca + cb < party)
                continue;
            if (!tableFreeForWindow(a._id, window, occupied))
                continue;
            if (!tableFreeForWindow(b._id, window, occupied))
                continue;
            push([a._id, b._id]);
        }
    }
    for (let i = 0; i < tables.length; i++) {
        for (let j = i + 1; j < tables.length; j++) {
            for (let k = j + 1; k < tables.length; k++) {
                const trip = [tables[i], tables[j], tables[k]];
                const sum = trip.reduce((s, t) => s + dineInTableCapacity(t, defaultSeatFallback), 0);
                if (sum < party)
                    continue;
                if (!trip.every((t) => tableFreeForWindow(t._id, window, occupied)))
                    continue;
                push(trip.map((t) => t._id));
            }
        }
    }
    return out;
}
//# sourceMappingURL=index.js.map